import { useCallback, useEffect, useRef } from "react";

type IntersectionObserverRefCallback<E extends Element> = (
  element: E | null
) => void;

/**
 * Usage
 * ```tsx
 * const MyComponent = () => {
 *     const elementRef = useIntersectionObserver((entry) => { ... do something ...}, options)
 *
 *     return <div ref={elementRef} />
 * }
 * ```
 * @param callback Callback gets fired everytime IntersectionObserver entry call is fired.
 * @param options Same as IntersectionObserverInit. NOTE: options are NOT reactive.
 */
export const useIntersectionObserver = <E extends Element>(
  callback: (
    entry: IntersectionObserverEntry,
    observer: IntersectionObserver
  ) => void,
  {
    threshold,
    rootMargin,
    root,
    resetKey,
  }: Readonly<IntersectionObserverInit & { resetKey?: unknown }> = {
    rootMargin: "0px",
  }
): IntersectionObserverRefCallback<E> => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Always use the latest callback, without having to need to run useEffect again
  const callBackRef = useRef(callback);
  // oxlint-disable-next-line react/react-compiler -- FIXME(CLI-289): auto-suppressed during shared-config migration.
  callBackRef.current = callback;

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return useCallback(
    (e: E | null) => {
      if (!e) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            callBackRef.current(entry, observer);
          });
        },
        { rootMargin, root, threshold }
      );
      observerRef.current?.disconnect();
      observer.observe(e);
      observerRef.current = observer;
    },
    // oxlint-disable-next-line react/react-compiler -- resetKey forces a fresh observer on an already-mounted, already-fired element without remounting it.
    [root, rootMargin, threshold, resetKey]
  );
};
